import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { NotificationDocumentType } from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { HOSPITAL_ABBREV_PROMPT, IS_NEWBORN_PROMPT } from "@/src/models/constants/prompt";
import { Specialty } from "@/src/models/constants/specialty";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { appointmentStatusSchema, notificationEncounterTypeSchema } from "@/src/models/intake/blocks/block";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const notificationTypeDisplay: Record<NotificationDocumentType, string> = {
  [NotificationDocumentType.APPOINTMENT_CANCELLED_MISSED]: "Appt Cancelled/Missed",
  [NotificationDocumentType.APPOINTMENT_OTHER]: "Appt Notification",
  [NotificationDocumentType.HOSPITAL_ADMIT]: "Hospital - Admit Notice",
  [NotificationDocumentType.HOSPITAL_DISCHARGE]: "Hospital - Discharge Notice",
  [NotificationDocumentType.HOSPITAL_ER_ADMIT]: "Hospital - ER Admit Notice",
  [NotificationDocumentType.HOSPITAL_ER_DISCHARGE]: "Hospital - ER Discharge Notice",
  [NotificationDocumentType.HOSPITAL_FACE_SHEET]: "Hospital - Face Sheet",
  [NotificationDocumentType.HOSPITAL_SDS_ADMIT]: "Hospital - SDS Admit Notification",
  [NotificationDocumentType.HOSPITAL_SDS_DISCHARGE]: "Hospital - SDS Discharge Notification",
  [NotificationDocumentType.NOTIFICATION]: "Notification",
};

export const notificationTypeSchema = z.nativeEnum(NotificationDocumentType);

const baseNotificationFields = {
  patient: patientMinimalSchema,
};

const extendedNotificationSchema = z.object({
  isAdw: isAdwSchema,
  hospitalAbbrev: z.string().nullable().describe(HOSPITAL_ABBREV_PROMPT),
  specialty: z.nativeEnum(Specialty).nullable(),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  encounterType: notificationEncounterTypeSchema.nullable(),
  isNewborn: z.boolean().nullable().describe(IS_NEWBORN_PROMPT),
  nameOfLab: z.string().nullable(),
  appointmentStatus: appointmentStatusSchema,
  signatureRequired: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
});

const notificationCustomFields: Record<NotificationDocumentType, Record<string, z.ZodTypeAny>> = {
  [NotificationDocumentType.HOSPITAL_ADMIT]: extendedNotificationSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    facilityType: true,
    encounterType: true,
    receivingProvider: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_DISCHARGE]: extendedNotificationSchema.pick({
    isAdw: true,
    dateOfEvent: true,
    hospitalAbbrev: true,
    receivingProvider: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_ER_ADMIT]: extendedNotificationSchema.pick({
    dateOfEvent: true,
    hospitalAbbrev: true,
    facilityType: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_ER_DISCHARGE]: extendedNotificationSchema.pick({
    dateOfEvent: true,
    hospitalAbbrev: true,
    facilityType: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_FACE_SHEET]: extendedNotificationSchema.pick({
    dateOfEvent: true,
    hospitalAbbrev: true,
  }).shape,
  [NotificationDocumentType.NOTIFICATION]: extendedNotificationSchema.shape,
  [NotificationDocumentType.APPOINTMENT_CANCELLED_MISSED]: extendedNotificationSchema.pick({
    dateReceived: true,
    specialty: true,
  }).shape,
  [NotificationDocumentType.APPOINTMENT_OTHER]: extendedNotificationSchema.pick({
    appointmentStatus: true,
    dateReceived: true,
    specialty: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_SDS_ADMIT]: extendedNotificationSchema.pick({
    dateOfEvent: true,
    hospitalAbbrev: true,
  }).shape,
  [NotificationDocumentType.HOSPITAL_SDS_DISCHARGE]: extendedNotificationSchema.pick({
    dateOfEvent: true,
    hospitalAbbrev: true,
  }).shape,
};

const makeNotificationSchema = (type: NotificationDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseNotificationFields,
    ...customFields,
  });

export const notificationSchemas: Record<NotificationDocumentType, ReturnType<typeof makeNotificationSchema>> = (
  Object.values(NotificationDocumentType) as NotificationDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeNotificationSchema(type, notificationCustomFields[type]);

    return acc;
  },
  {} as Record<NotificationDocumentType, ReturnType<typeof makeNotificationSchema>>
);

const notificationSchemasArray = (Object.values(NotificationDocumentType) as NotificationDocumentType[]).map(
  (type) => notificationSchemas[type]
) as [ReturnType<typeof makeNotificationSchema>, ...ReturnType<typeof makeNotificationSchema>[]];

export const anyNotificationSchema = z.discriminatedUnion("type", notificationSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type Notification = ExtractedDocumentFields<
  NotificationDocumentType,
  typeof extendedNotificationSchema,
  typeof baseNotificationFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const notificationShape: Notification = {
  id: "",
  type: NotificationDocumentType.NOTIFICATION,
  patient: null,
  hospitalAbbrev: null,
  specialty: null,
  provider: null,
  receivingProvider: null,
  facilityType: null,
  encounterType: null,
  isNewborn: null,
  nameOfLab: null,
  dateOfDocument: null,
  dateOfEvent: null,
  dateReceived: null,
  dateSigned: null,
  signatureRequired: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
